import { useCallback, useMemo } from "react";

import type { ScorecardSourceOption } from "./ScorecardSourceSelector";

import { BlockStoreProvider } from "@/components/PowerBlock/Block/block-store";
import InputVariablesField from "@/components/PowerBlock/Block/InputVariablesField";
import {
  SourceType,
  type SourcesV2,
} from "@/components/PowerBlock/Block/SourcesField/SelectSource";
import { SourcesV2Provider } from "@/components/PowerBlock/Block/SourcesField/sourcesV2Context";

interface VariableMappingConfiguration {
  variable_mappings?: Record<string, string>;
}

const createScorecardSources = (
  options: ScorecardSourceOption[],
): Record<SourceType, string[]> => ({
  [SourceType.Column]: options.map(option => option.id),
  [SourceType.Dataset]: [],
  [SourceType.InputVariable]: [],
  [SourceType.Dependency]: [],
});

const createScorecardSourcesV2 = (options: ScorecardSourceOption[]): SourcesV2 => ({
  [SourceType.Column]: options.map(option => ({
    id: option.id,
    label: option.title,
    value: option.id,
  })),
});

interface ScorecardInputVariablesFieldProps {
  variables: string[];
  sourceColumnOptions: ScorecardSourceOption[];
  variableMappings: Record<string, string>;
  onVariableMappingsChange: (mappings: Record<string, string>) => void;
}

export const ScorecardInputVariablesField = ({
  variables,
  sourceColumnOptions,
  variableMappings,
  onVariableMappingsChange,
}: ScorecardInputVariablesFieldProps) => {
  const initialConfiguration = useMemo<VariableMappingConfiguration>(
    () => ({ variable_mappings: variableMappings }),
    [variableMappings],
  );
  const sources = useMemo(() => createScorecardSources(sourceColumnOptions), [sourceColumnOptions]);
  const sourcesV2 = useMemo(
    () => createScorecardSourcesV2(sourceColumnOptions),
    [sourceColumnOptions],
  );

  const handleChange = useCallback(
    (payload: { configuration: VariableMappingConfiguration }) => {
      onVariableMappingsChange(payload.configuration.variable_mappings ?? {});
    },
    [onVariableMappingsChange],
  );

  const handleAutofill = useCallback(
    (mappings: Record<string, string>) => {
      onVariableMappingsChange({ ...variableMappings, ...mappings });
    },
    [onVariableMappingsChange, variableMappings],
  );

  return (
    <BlockStoreProvider<VariableMappingConfiguration>
      initialConfiguration={initialConfiguration}
      onChange={handleChange}
    >
      <SourcesV2Provider value={sourcesV2}>
        <InputVariablesField
          variables={variables}
          sources={sources}
          sourceKeyPrefix="variable_mappings"
          showAutofill
          onAutofill={handleAutofill}
          templateFormat="f-string"
        />
      </SourcesV2Provider>
    </BlockStoreProvider>
  );
};
